import { useT } from "../../utilities/TranslatedText";
import { PlaceholderPlugin } from "@platejs/media/react";
import { useMemo, useRef, useState } from "react";
import {
  useEditorPlugin,
  useRemoveNodeButton,
  useElement,
  useEditorRef,
} from "platejs/react";
import styles from "../styles/SlateEditor.module.scss";
import { Popover } from "radix-ui";
import FontAwesome from "../../utilities/FontAwesome";
import { ButtonWrapper } from "../toolbar/buttons/_Button";
import { compressImage } from "../../../utils/DataUtilities";
import NotificationManager from "../../notifications/NotificationManager";
import { reportError } from "../../../utils/errorReporting";

export function MediaPlaceholder(props) {
  const { element } = props;
  const { api } = useEditorPlugin(PlaceholderPlugin);

  const imageSrc = useMemo(() => {
    const currentFile = api.placeholder.getUploadingFile(element.id as string);
    if (currentFile) {
      {
        return URL.createObjectURL(currentFile);
      }
    }
  }, []);

  if (!imageSrc) {
    return null;
  }

  return <PlateImageElement src={imageSrc} />;
}

export function PlateImageElement(props: { src: string; [key: string]: any }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editor = useEditorRef();
  const element = useElement();
  const { props: buttonProps } = useRemoveNodeButton({ element });
  const [isEditingAltText, setIsEditingAltText] = useState(false);
  const elementAttributes = element.elementAttributes as
    | React.HTMLAttributes<HTMLImageElement>
    | undefined;
  const decorative = elementAttributes?.role === "presentation";
  const [draftDecorative, setDraftDecorative] = useState(decorative);
  const [altText, setAltText] = useState<string>((element.alt as string) || "");

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <figure contentEditable={false} className={styles.figure}>
        {!(element.alt as string)?.trim() && !decorative && (
          <div>
            <FontAwesome prefix={"fas"} name={"fa-triangle-exclamation"} />
          </div>
        )}
        <Popover.Trigger asChild>
          <button
            type="button"
            className={styles.imageTrigger}
            aria-label={
              (element.alt as string)?.trim() || decorative
                ? t("Edit image")
                : t("Edit image, missing alt text")
            }
          >
            <img
              {...props}
              alt={(element.alt as string) || ""}
              src={props.src}
            />
          </button>
        </Popover.Trigger>
      </figure>
      <Popover.Portal>
        <Popover.Content>
          <div className={styles.imageUploadPopover}>
            {isEditingAltText ? (
              <div className={styles.altTextEditor}>
                <ButtonWrapper
                  aria-label={t("Back to image options")}
                  title={t("Back to image options")}
                  onClick={() => {
                    setIsEditingAltText(false);
                  }}
                >
                  <FontAwesome
                    prefix={"fas"}
                    name={"fa-rotate-left"}
                    fixedWidth
                  />
                </ButtonWrapper>
                <label>
                  <input
                    type="checkbox"
                    checked={draftDecorative}
                    onChange={(e) => setDraftDecorative(e.target.checked)}
                  />
                  {t("Decorative image (no description needed)")}
                </label>
                <input
                  aria-label={t("Alt text")}
                  disabled={draftDecorative}
                  type={"text"}
                  placeholder={t("Describe this image")}
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                />
                <ButtonWrapper
                  aria-label={t("Save alt text")}
                  onClick={() => {
                    editor.tf.setNodes(
                      {
                        alt: draftDecorative ? "" : altText.trim(),
                        elementAttributes: {
                          ...elementAttributes,
                          role: draftDecorative ? "presentation" : undefined,
                        },
                      },
                      {
                        at: element,
                      },
                    );
                    setIsEditingAltText(false);
                  }}
                >
                  {t("Save")}
                </ButtonWrapper>
              </div>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={"image/*"}
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    try {
                      // Compress to 4MB — @platejs/media's PlaceholderPlugin silently rejects larger images.
                      // Default is `uploadConfig.image.maxFileSize: "4MB"` (see node_modules/@platejs/media/dist/react/index.js).
                      //   uploadConfig: {
                      //   ...
                      //     image: {
                      //       maxFileCount: 3,
                      //           maxFileSize: "4MB",
                      //           mediaType: KEYS.img,
                      //           minFileCount: 1
                      //     },
                      //   ...
                      //   }
                      const compressed = await Promise.all(
                        Array.from(files).map((f) => compressImage(f, 4)),
                      );
                      const path = editor.api.findPath(element);
                      editor
                        .getTransforms(PlaceholderPlugin)
                        .insert.media(compressed as unknown as FileList);
                      editor.tf.removeNodes({ at: path });
                    } catch (err) {
                      reportError(err, { error_source: "handled" });
                      NotificationManager.error(
                        err instanceof Error
                          ? err.message
                          : "Image upload failed.",
                      );
                    }
                  }}
                />
                <ButtonWrapper
                  onClick={() => {
                    setAltText((element.alt as string) || "");
                    setDraftDecorative(decorative);
                    setIsEditingAltText(true);
                  }}
                >
                  {t("Edit Alt Text")}
                </ButtonWrapper>
                <ButtonWrapper
                  aria-label={t("Replace image")}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FontAwesome fixedWidth prefix={"fas"} name={"fa-upload"} />
                </ButtonWrapper>
                <ButtonWrapper {...buttonProps} aria-label={t("Remove image")}>
                  <FontAwesome fixedWidth prefix={"fas"} name={"fa-trash"} />
                </ButtonWrapper>
              </>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
